/**
 * A conferência final antes da assinatura (E169.F1, GUS-113 e GUS-215, ADR-0117). Espelha
 * `apps/api/types/conferencia_final.py`.
 *
 * - `GET /casos/:id/conferencia-final`: a versão, as duas saídas com a marca do perito, as revisões
 *   RP em vigor e as pendentes, a assinatura pela prontidão e quem pode assinar.
 * - `POST /casos/:id/conferencia-final`: confirma a versão conferida e leva o caso à
 *   assinatura; 409 com a versão mudada, 422 com a prontidão que segura.
 * - `POST /casos/:id/saidas/:saida/revisao`: marca a saída como revisada, sobre a prévia aberta.
 *
 * Nada é decidido aqui: o estado e os motivos chegam prontos do servidor.
 */
import axios, { type AxiosInstance } from 'axios';

import type { StatusCertificacao } from '@/lib/certificacao';
import type { SaidaDoPacote } from '@/lib/preparacao_do_pacote';
import type { LiberacaoDaSaidaData, MotivoDaProntidao } from '@/lib/resultadoAuditado';

export type SituacaoDaSaidaNaConferencia =
  | 'nao_se_aplica'
  | 'nao_preparada'
  | 'conferencia_pendente'
  | 'por_revisar'
  | 'revisada';

export type MotivoSemAssinar = 'nao_certificador' | 'sem_cpf';
export type EstadoDaConferenciaDaPrevia = 'aprovado' | 'corrigir' | 'bloqueado';
export type ConclusaoDaFormalizacao = 'regular' | 'irregular' | 'nao_demonstrada' | 'pendente';
export type CodigoDaRevisao = 'RP-04' | 'RP-08';
export type TipoDaRevisao =
  | 'face'
  | 'contraprova'
  | 'suprimento'
  | 'diligencias'
  | 'autorizacao'
  | 'credito'
  | 'integridade';

export interface SaidaNaConferenciaFinalData {
  saida: SaidaDoPacote;
  situacao: SituacaoDaSaidaNaConferencia;
  artefato_id?: string | null;
  versao_do_artefato?: number | null;
  conferencia?: EstadoDaConferenciaDaPrevia | null;
  revisada_por?: string | null;
  revisada_em?: string | null;
}

export interface RevisaoPericialNaConferenciaData {
  codigo: CodigoDaRevisao | null;
  tipo: TipoDaRevisao;
  decisao: string;
  revisor?: string | null;
  em?: string | null;
}

export interface ConferenciaFinalData {
  case_id: string;
  display_code: string;
  versao?: number | null;
  avaliacao_id?: string | null;
  resultado_sha256?: string | null;
  modo: 'parte' | 'juizo';
  conclusao?: ConclusaoDaFormalizacao | null;
  saidas: SaidaNaConferenciaFinalData[];
  revisoes_em_vigor: RevisaoPericialNaConferenciaData[];
  revisoes_pendentes: string[];
  assinatura: LiberacaoDaSaidaData;
  requisicao?: StatusCertificacao | null;
  /** E170.F1.C04 (ADR-0123): a requisição, para a revogação da certificação entregue. */
  requisicao_id?: string | null;
  pode_assinar: boolean;
  sem_assinar?: MotivoSemAssinar | null;
}

export interface ConfirmacaoDaConferenciaData {
  requisicao_id: string;
  status: StatusCertificacao;
  pode_assinar: boolean;
  sem_assinar?: MotivoSemAssinar | null;
}

export interface RevisaoDaSaidaData {
  id: string;
  case_id: string;
  saida: SaidaDoPacote;
  avaliacao_id: string;
  resultado_sha256: string;
  artefato_id: string;
  revisor: string;
  revisor_nome: string;
  criada_em: string;
}

/** Por que a leitura falhou. */
export type FalhaDaConferenciaFinal = 'sem_permissao' | 'nao_encontrado' | 'indisponivel';

/** Por que a marca ou a confirmação foi recusada, pelo código do servidor. */
export type RecusaDaConferenciaFinal =
  | 'previa_nao_aberta'
  | 'previa_de_outra_versao'
  | 'conferencia_nao_aprovada'
  | 'nao_se_aplica'
  | 'sem_avaliacao'
  | 'versao_desatualizada'
  | 'nao_pronta'
  | 'fora_do_portal'
  | 'sem_ccb'
  | 'indisponivel';

export class ConferenciaFinalError extends Error {
  constructor(
    readonly motivo: FalhaDaConferenciaFinal | RecusaDaConferenciaFinal,
    readonly pendencia: MotivoDaProntidao | null = null,
  ) {
    super(motivo);
    this.name = 'ConferenciaFinalError';
  }
}

const RECUSAS: ReadonlySet<string> = new Set<RecusaDaConferenciaFinal>([
  'previa_nao_aberta',
  'previa_de_outra_versao',
  'conferencia_nao_aprovada',
  'nao_se_aplica',
  'sem_avaliacao',
  'versao_desatualizada',
  'nao_pronta',
  'fora_do_portal',
  'sem_ccb',
]);

function daRecusa(codigo: string, pendencia: MotivoDaProntidao | null = null) {
  const motivo = codigo.toLowerCase();
  return RECUSAS.has(motivo)
    ? new ConferenciaFinalError(motivo as RecusaDaConferenciaFinal, pendencia)
    : new ConferenciaFinalError('indisponivel');
}

/** O código do servidor (`PREVIA_NAO_ABERTA`, `{code: NAO_PRONTA, pendencia}`) → a recusa. */
export function recusaDoServidor(err: unknown): ConferenciaFinalError {
  if (!axios.isAxiosError(err)) return new ConferenciaFinalError('indisponivel');
  const status = err.response?.status;
  if (status === 403) return new ConferenciaFinalError('sem_permissao');
  if (status === 404) return new ConferenciaFinalError('nao_encontrado');
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detalhe === 'string') return daRecusa(detalhe);
  if (detalhe !== null && typeof detalhe === 'object' && 'code' in detalhe) {
    const { code, pendencia } = detalhe as { code: string; pendencia?: MotivoDaProntidao | null };
    return daRecusa(code, pendencia ?? null);
  }
  return new ConferenciaFinalError('indisponivel');
}

export async function lerConferenciaFinal(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaFinalData> {
  try {
    const { data } = await client.get<ConferenciaFinalData>(`/casos/${caseId}/conferencia-final`);
    return data;
  } catch (err) {
    throw recusaDoServidor(err);
  }
}

export async function marcarSaidaRevisada(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDoPacote,
  artefatoId: string,
): Promise<RevisaoDaSaidaData> {
  try {
    const { data } = await client.post<RevisaoDaSaidaData>(
      `/casos/${caseId}/saidas/${saida}/revisao`,
      { artefato_id: artefatoId },
    );
    return data;
  } catch (err) {
    throw recusaDoServidor(err);
  }
}

export async function confirmarConferenciaFinal(
  client: AxiosInstance,
  caseId: string,
  conferencia: Pick<ConferenciaFinalData, 'avaliacao_id' | 'resultado_sha256'>,
): Promise<ConfirmacaoDaConferenciaData> {
  try {
    const { data } = await client.post<ConfirmacaoDaConferenciaData>(
      `/casos/${caseId}/conferencia-final`,
      {
        avaliacao_id: conferencia.avaliacao_id,
        resultado_sha256: conferencia.resultado_sha256,
        confirmo: true,
      },
    );
    return data;
  } catch (err) {
    throw recusaDoServidor(err);
  }
}

/** Toda saída aplicável revisada: a confirmação só é oferecida assim. */
export function saidasRevisadas(conferencia: ConferenciaFinalData): boolean {
  return conferencia.saidas.every(
    (s) => s.situacao === 'revisada' || s.situacao === 'nao_se_aplica',
  );
}

/** Por que a revogação foi recusada (E170.F1.C04, ADR-0123). */
export type RecusaDaRevogacao =
  | 'nao_revogavel'
  | 'motivo_invalido'
  | 'sem_permissao'
  | 'indisponivel';

export class RevogacaoError extends Error {
  constructor(readonly motivo: RecusaDaRevogacao) {
    super(motivo);
    this.name = 'RevogacaoError';
  }
}

function recusaDaRevogacao(err: unknown): RevogacaoError {
  if (!axios.isAxiosError(err)) return new RevogacaoError('indisponivel');
  const status = err.response?.status;
  if (status === 403) return new RevogacaoError('sem_permissao');
  if (status === 409) return new RevogacaoError('nao_revogavel');
  if (status === 422) return new RevogacaoError('motivo_invalido');
  return new RevogacaoError('indisponivel');
}

/** Mínimo de caracteres do motivo, o mesmo do servidor (migration 0161). */
export const MOTIVO_MINIMO_DA_REVOGACAO = 20;

/**
 * Revoga a certificação entregue: a requisição volta a aguardar a assinatura, a conclusão pode ser reaberta e a
 * nova assinatura substitui a revogada. Nada se apaga (E170.F1.C02, ADR-0123).
 */
export async function revogarCertificacao(
  client: AxiosInstance,
  requisicaoId: string,
  motivo: string,
): Promise<void> {
  try {
    await client.post(`/certificacao/requisicoes/${requisicaoId}/revogacao`, { motivo });
  } catch (err) {
    throw recusaDaRevogacao(err);
  }
}
